/**
 * EmailCategoriesPanel
 * ──────────────────────────────────────────────────────────
 * Staff-facing tab inside the Unsubscribe Dashboard. Surfaces:
 *
 *   • The canonical catalogue of email categories (the same
 *     source-of-truth used by every sender + the customer
 *     preference centre).
 *   • Per-category opt-out counts (how many customers have
 *     muted each marketing category).
 *   • A quick-look at the legacy "muted ALL marketing" count
 *     and the hard-block (scope=all) count.
 *
 * Read-only catalogue — actual customer prefs are edited from
 * the customer preference center or by removing rows on the
 * other tab.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import { Loader2, Lock, ShieldCheck, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

export default function EmailCategoriesPanel() {
  const token = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/email-preferences/admin/stats/by-category`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch (e) {
      toast.error("Could not load email category stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const operational = (stats.categories || []).filter((c) => c.kind === "operational");
  const marketing = (stats.categories || []).filter((c) => c.kind === "marketing");
  const internalCats = (stats.categories || []).filter((c) => c.kind === "internal");

  return (
    <div data-testid="email-categories-panel">
      {/* Top recap */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">Total categories</div>
          <div className="text-3xl font-bold text-slate-900">{(stats.categories || []).length}</div>
          <div className="text-xs text-slate-500 mt-1">
            {operational.length} operational · {marketing.length} marketing · {internalCats.length} internal
          </div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs text-amber-700 uppercase tracking-wide">Muted ALL marketing</div>
          <div className="text-3xl font-bold text-amber-800">{stats.all_marketing_blocked || 0}</div>
          <div className="text-xs text-amber-700 mt-1">legacy full unsubscribe</div>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="text-xs text-red-700 uppercase tracking-wide">Hard-blocked (all)</div>
          <div className="text-3xl font-bold text-red-800">{stats.all_emails_blocked || 0}</div>
          <div className="text-xs text-red-700 mt-1">incl. operational</div>
        </div>
      </div>

      {/* Refresh */}
      <div className="flex justify-end mb-3">
        <Button variant="outline" size="sm" onClick={load} data-testid="cats-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Marketing */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          <span className="font-semibold text-slate-900">Marketing &amp; informational</span>
          <span className="text-xs text-slate-500">— customer can opt-out per category</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="px-4 py-2 text-left">Category</th>
              <th className="px-4 py-2 text-left">Description</th>
              <th className="px-4 py-2 text-right">Opted out</th>
            </tr>
          </thead>
          <tbody>
            {marketing.map((cat) => (
              <tr key={cat.id} className="border-t" data-testid={`cat-stat-${cat.id}`}>
                <td className="px-4 py-2 font-medium">{cat.label}</td>
                <td className="px-4 py-2 text-slate-600 text-xs">{cat.description}</td>
                <td className="px-4 py-2 text-right font-semibold text-amber-700">
                  {stats.by_category?.[cat.id] || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Operational */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
          <Lock className="h-4 w-4 text-slate-500" />
          <span className="font-semibold text-slate-900">Operational</span>
          <span className="text-xs text-slate-500">— always sent to customers, cannot be opted out (unless hard-blocked)</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="px-4 py-2 text-left">Category</th>
              <th className="px-4 py-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            {operational.map((cat) => (
              <tr key={cat.id} className="border-t">
                <td className="px-4 py-2 font-medium">{cat.label}</td>
                <td className="px-4 py-2 text-slate-600 text-xs">{cat.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Internal */}
      {internalCats.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
            <Lock className="h-4 w-4 text-violet-500" />
            <span className="font-semibold text-slate-900">Internal (staff-only)</span>
            <span className="text-xs text-slate-500">— not shown in the customer preference centre</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="px-4 py-2 text-left">Category</th>
                <th className="px-4 py-2 text-left">Description</th>
              </tr>
            </thead>
            <tbody>
              {internalCats.map((cat) => (
                <tr key={cat.id} className="border-t">
                  <td className="px-4 py-2 font-medium">{cat.label}</td>
                  <td className="px-4 py-2 text-slate-600 text-xs">{cat.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
